/**
 * OFX delivery estimator.
 *
 * Fetches the store's native shipping rates for a Puerto Rico ZIP code via
 * Shopify's cart AJAX endpoints (prepare_shipping_rates + async_shipping_rates).
 * Rates always mirror admin → Settings → Shipping and delivery; there is no
 * app dependency and no rate data in the theme.
 */
class OfxDeliveryEstimator extends HTMLElement {
  #abort = null;

  connectedCallback() {
    if (this.dataset.bound) return;
    this.dataset.bound = 'true';

    this.refs = {};
    for (const el of this.querySelectorAll('[data-ofx-ref]')) {
      this.refs[el.getAttribute('data-ofx-ref')] = el;
    }

    this.refs.button?.addEventListener('click', () => this.check());
    this.refs.zip?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.check();
      }
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get #texts() {
    return this.dataset;
  }

  async check() {
    const { zip: zipInput, button, output, hint } = this.refs;
    if (!zipInput || !button || !output) return;

    // Cancel any in-flight check first so a stale response can never
    // overwrite whatever this call renders (including the invalid state).
    this.#abort?.abort();
    this.#abort = null;
    button.disabled = false;
    button.textContent = this.#texts.txtCheck;

    const zip = zipInput.value.trim();
    zipInput.classList.remove('ofx-delivery-est--invalid');
    output.innerHTML = '';

    if (!/^\d{5}$/.test(zip)) {
      zipInput.classList.add('ofx-delivery-est--invalid');
      this.#renderMessage('error', this.#texts.txtInvalid);
      return;
    }

    const abort = new AbortController();
    this.#abort = abort;

    button.disabled = true;
    button.innerHTML = '';
    button.append(this.#spinner(), ` ${this.#texts.txtChecking}`);

    try {
      const rates = await this.#fetchRates(zip, abort.signal);
      if (abort.signal.aborted) return;

      if (rates === null) {
        this.#renderMessage('info', null);
      } else if (rates.length === 0) {
        this.#renderMessage('info', null);
      } else {
        this.#renderRates(zip, rates);
        if (hint) hint.hidden = true;
      }
    } catch (error) {
      if (!abort.signal.aborted) {
        this.#renderMessage('error', this.#texts.txtError);
      }
    } finally {
      if (this.#abort === abort && !abort.signal.aborted) {
        button.disabled = false;
        button.textContent = this.#texts.txtCheck;
      }
    }
  }

  /**
   * Clears a shown estimate and returns to the input state without stealing
   * focus. Called by the cart drawer whenever the cart contents change,
   * because shipping rates are cart-dependent.
   */
  invalidate() {
    this.#abort?.abort();
    this.#abort = null;
    const { output, form, hint, button } = this.refs;
    if (output) output.innerHTML = '';
    if (form) form.hidden = false;
    if (hint) hint.hidden = false;
    if (button) {
      button.disabled = false;
      button.textContent = this.#texts.txtCheck;
    }
  }

  /**
   * @returns {Promise<Array|null>} rates array, or null when the address is
   * outside the store's delivery area (Shopify responds 422).
   */
  async #fetchRates(zip, signal) {
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': 'United States',
      'shipping_address[province]': 'Puerto Rico',
    });

    const prepare = await fetch(`${window.Shopify?.routes?.root || '/'}cart/prepare_shipping_rates.json?${params}`, {
      method: 'POST',
      signal,
    });

    if (prepare.status === 422) return null;
    if (!prepare.ok && prepare.status !== 202) throw new Error(`prepare failed: ${prepare.status}`);

    for (let attempt = 0; attempt < 14; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 750));
      if (signal.aborted) return [];

      const poll = await fetch(`${window.Shopify?.routes?.root || '/'}cart/async_shipping_rates.json?${params}`, { signal });
      if (poll.status === 422) return null;
      if (!poll.ok) continue;

      const body = await poll.json();
      if (body && Array.isArray(body.shipping_rates)) return body.shipping_rates;
    }

    throw new Error('rates polling timed out');
  }

  #renderRates(zip, rates) {
    const { output } = this.refs;
    const texts = this.#texts;

    const wrap = document.createElement('div');
    wrap.className = 'ofx-delivery-est__results';

    const forRow = document.createElement('div');
    forRow.className = 'ofx-delivery-est__for';
    const forLabel = document.createElement('span');
    forLabel.append(`${texts.txtOptionsFor} `);
    const zipEl = document.createElement('b');
    zipEl.textContent = zip;
    forLabel.append(zipEl);
    const changeBtn = document.createElement('button');
    changeBtn.type = 'button';
    changeBtn.textContent = texts.txtChange;
    changeBtn.addEventListener('click', () => this.#reset());
    forRow.append(forLabel, changeBtn);
    wrap.append(forRow);

    const formatter = this.#formatter();
    for (const rate of rates) {
      const row = document.createElement('div');
      row.className = 'ofx-delivery-est__rate';
      const name = document.createElement('span');
      name.className = 'ofx-delivery-est__rate-name';
      name.textContent = rate.presentment_name || rate.name || '';
      const amt = document.createElement('span');
      const price = Number.parseFloat(rate.price);
      if (price === 0) {
        amt.className = 'ofx-delivery-est__rate-amt ofx-delivery-est__rate-amt--free';
        amt.textContent = texts.txtFree;
      } else {
        amt.className = 'ofx-delivery-est__rate-amt';
        amt.textContent = Number.isNaN(price) ? rate.price : formatter.format(price);
      }
      row.append(name, amt);
      wrap.append(row);
    }

    const fineprint = document.createElement('p');
    fineprint.className = 'ofx-delivery-est__fineprint';
    fineprint.textContent = texts.txtFineprint;
    wrap.append(fineprint);

    output.innerHTML = '';
    output.append(wrap);
    if (this.refs.form) this.refs.form.hidden = true;
  }

  #renderMessage(kind, text) {
    const { output } = this.refs;
    const texts = this.#texts;

    const msg = document.createElement('div');
    msg.className = `ofx-delivery-est__msg ofx-delivery-est__msg--${kind}`;

    const ico = document.createElement('span');
    ico.className = 'ofx-delivery-est__msg-ico';
    ico.setAttribute('aria-hidden', 'true');
    ico.innerHTML =
      kind === 'info'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>';

    const body = document.createElement('span');
    if (kind === 'info' && text === null) {
      const lead = document.createElement('b');
      lead.textContent = texts.txtPrOnly;
      body.append(lead, ` ${texts.txtPrOnlyHelp} `);
      const link = document.createElement('a');
      link.href = this.dataset.contactUrl || '/pages/contact-us';
      link.textContent = texts.txtContact;
      body.append(link);
    } else {
      body.textContent = text;
    }

    msg.append(ico, body);
    output.innerHTML = '';
    output.append(msg);
  }

  #reset() {
    const { output, form, zip, hint } = this.refs;
    if (output) output.innerHTML = '';
    if (form) form.hidden = false;
    if (hint) hint.hidden = false;
    if (zip) {
      zip.value = '';
      zip.classList.remove('ofx-delivery-est--invalid');
      zip.focus();
    }
  }

  #spinner() {
    const spin = document.createElement('span');
    spin.className = 'ofx-delivery-est__spin';
    spin.setAttribute('aria-hidden', 'true');
    return spin;
  }

  #formatter() {
    let locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });
    } catch (error) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }
  }
}

if (!customElements.get('ofx-delivery-estimator')) {
  customElements.define('ofx-delivery-estimator', OfxDeliveryEstimator);
}
